import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap
} from 'rxjs/operators';
import { Subject, combineLatest, of } from 'rxjs';
import { GoogleAnalyticsService } from '@app/shared/services/vendor';
import { ID } from '@state/base';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Md5 } from 'ts-md5';

import { ApplicationRepository, ApplicationService } from '@state/application';
import { ClientNotificationsRepository, ClientNotificationsService } from '@state/client-notifications';
import { environment } from '@env/environment';
import { HotjarService } from '@state/hotjar';
import { PusherService } from '@state/pusher';
import { RollbarService } from '@state/rollbar';
import { SessionRepository } from '@state/session';
import { SignatureRequestRepository } from '@state/signature-request';
import { TaskRepository } from '@state/task';
import { UserRepository, UserService } from '@state/user';
import { version } from '@env/version';
import { ZendeskService } from '@state/zendesk';
import { MatProgressBar } from '@angular/material/progress-bar';
import { AsyncPipe } from '@angular/common';
import { InvestorBulkEmailProgressComponent } from '@component/investor-bulk-email-progress';
import { ClientNotificationsBannerComponent } from './component/client-notifications-banner/client-notifications-banner.component';
import { ImpersonationBannerComponent } from './component/impersonation-banner/impersonation-banner.component';
import { FourOhFourComponent } from './route/four-oh-four/four-oh-four.component';
import { BlankLayoutContainerComponent } from './layout/blank-layout/blank-layout-container/blank-layout-container.component';
import { NonePipe } from './pipe/none/none.pipe';
import { HasAnyPipe } from './pipe/has-any/has-any.pipe';

const CLIENT_NOTIFICATION_EXCLUDED_ROUTES = ['/builder/', '/share'];

declare let window: any;

@Component({
  encapsulation: ViewEncapsulation.None,
  imports: [
    ClientNotificationsBannerComponent,
    ImpersonationBannerComponent,
    InvestorBulkEmailProgressComponent,
    FourOhFourComponent,
    BlankLayoutContainerComponent,
    MatProgressBar,
    RouterOutlet,
    AsyncPipe,
    NonePipe,
    HasAnyPipe
  ],
  selector: 'fl-documents-app',
  styleUrls: ['./documents-app.component.scss'],
  templateUrl: './documents-app.component.html'
})
export class DocumentsAppComponent implements OnInit {
  private applicationRepository = inject(ApplicationRepository);
  private applicationService = inject(ApplicationService);
  private clientNotificationsRepository = inject(ClientNotificationsRepository);
  private clientNotificationsService = inject(ClientNotificationsService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private hotjarService = inject(HotjarService);
  private pusherService = inject(PusherService);
  private route = inject(ActivatedRoute);
  private sessionRepository = inject(SessionRepository);
  private signatureRequestRepository = inject(SignatureRequestRepository);
  private taskRepository = inject(TaskRepository);
  private userRepository = inject(UserRepository);
  private userService = inject(UserService);
  private zendeskService = inject(ZendeskService);

  private refreshClientNotifications$ = new Subject<void>();

  notFound$ = this.applicationRepository.notFound$;
  requests$ = this.applicationRepository.requests$.pipe(debounceTime(0));

  rollbar = (window.globalRollbar = inject(RollbarService));
  router = (window.globalRouter = inject(Router));

  hasActiveClientNotifications$ = this.clientNotificationsRepository.hasActiveClientNotifications$;
  showClientNotifications$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(null),
    map(() => !CLIENT_NOTIFICATION_EXCLUDED_ROUTES.some((route) => this.router.url.includes(route)))
  );
  snackBar = (window.globalSnackBar = inject(MatSnackBar));

  ngOnInit() {
    if (this.sessionRepository.loggedIn()) {
      this.applicationService.getAndStoreGlobalOrganizationId$().subscribe();
      this.userService.setUserFromAuth();
    }

    this.googleAnalyticsService.loadGtagScript();
    this.watchUserForClientNotifications();
    this.watchRouteChanges();
    this.watchApplicationChannel();

    if (window.navigator.webdriver) this.setHeadlessStyles();
    window.fl = { version };
  }

  showUpgradeModal = (): void => {
    this.snackBar
      .open('A newer version of Flow is available!', 'Refresh', { duration: 259200 })
      .onAction()
      .subscribe({ next: () => location.reload() });
  };

  identifyHotJarUser(): void {
    let user_id = this.userRepository.getUser()?.user_id;
    let investment_id: ID;
    let vehicle_id: ID;
    let fundId = this.route.snapshot.firstChild?.params?.fund_id;

    let { task } = this.taskRepository.getValue();
    let { signatureRequest } = this.signatureRequestRepository.getValue();

    if (fundId) vehicle_id = fundId;

    if (task) {
      investment_id = task.investment_id;
      vehicle_id = task.vehicle?.id;
    }

    if (signatureRequest) {
      investment_id = signatureRequest.investment_id;
      vehicle_id = signatureRequest.vehicle_id;
    }

    this.hotjarService.identify(user_id, {
      admin: this.userRepository.isAdmin(),
      doc_admin: this.userRepository.isDocAdmin(),
      environment: environment.runtimeEnv,
      investment_id,
      user_id,
      vehicle_id
    });
  }

  setHeadlessStyles(): void {
    let headlessStyles = document.createElement('link');
    headlessStyles.rel = 'stylesheet';
    // Relative (not '/assets/...') so it resolves against the app's <base href>
    // — the app is served under a base path (e.g. /flowui/) behind the proxy.
    headlessStyles.href = 'assets/headless.css';
    document.head.appendChild(headlessStyles);
  }

  // Banners come from the server and require an authenticated principal, so
  // load them whenever a user becomes known (app boot, login, signup, guards)
  // or the server pushes a banner change, and clear them when the user is
  // unset (logout).
  watchUserForClientNotifications(): void {
    let userId$ = this.userRepository.user$.pipe(
      map((user) => user?.user_id ?? user?.id),
      distinctUntilChanged()
    );
    let refresh$ = this.refreshClientNotifications$.pipe(startWith(undefined));

    combineLatest([userId$, refresh$])
      .pipe(
        switchMap(([userId]) => {
          if (!userId) {
            this.clientNotificationsService.reset();
            return of([]);
          }

          return this.clientNotificationsService.fetchClientNotifications$().pipe(catchError(() => of([])));
        })
      )
      .subscribe();
  }

  watchRouteChanges(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe({
      next: () => {
        this.identifyHotJarUser();
      }
    });
  }

  // App-wide channel shared with the API (see PusherClient.primary_qualifier and
  // Shared::Banner in flowapi): `update` announces a new release,
  // `update_notifications` a banner change. The key must hash the same string
  // the API does, application/doc/<PRIMARY_QUALIFIER>.
  watchApplicationChannel(): void {
    let channelId = Md5.hashStr(`application/doc/${environment.primaryQualifier}`);
    let channel = this.pusherService.pusher.subscribe(channelId);

    channel.bind('update', this.showUpgradeModal);
    channel.bind('update_notifications', () => this.refreshClientNotifications$.next());
  }
}
